import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Star, Eye, Moon, Sun, Heart, Coins, Shuffle, RotateCcw, Loader2, Sparkles, TreeDeciduous } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API, getCardSymbol } from '../utils/helpers';
import { ShareButton } from '../components/ShareComponents';
import MarkdownRenderer from '../components/MarkdownRenderer';

export default function TarotPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [spreads, setSpreads] = useState({});
  const [selectedSpread, setSelectedSpread] = useState(null);
  const [stage, setStage] = useState('select');
  const [deck, setDeck] = useState([]);
  const [selectedCards, setSelectedCards] = useState([]);
  const [interpretation, setInterpretation] = useState('');
  const [loading, setLoading] = useState(false);
  const [canReadFree, setCanReadFree] = useState(true);
  const deckContainerRef = useRef(null);
  const [deckWidth, setDeckWidth] = useState(800);
  const [isShuffling, setIsShuffling] = useState(false);

  useEffect(() => {
    fetch(`${API}/tarot/spreads`).then(res => res.json()).then(setSpreads);
    fetch(`${API}/tarot/cards`).then(res => res.json()).then(setDeck);
    fetch(`${API}/tarot/free-limit`, { credentials: 'include' }).then(res => res.json()).then(data => setCanReadFree(data.can_read_free)).catch(() => {});
  }, []);

  useEffect(() => {
    const measure = () => { if (deckContainerRef.current) setDeckWidth(deckContainerRef.current.offsetWidth - 32); };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [stage]);

  const spreadList = [
    { key: 'three_cards', icon: Star, desc: 'Pasado, Presente y Futuro' },
    { key: 'yes_no', icon: Eye, desc: 'Respuesta rapida' },
    { key: 'love', icon: Heart, desc: 'Relaciones y amor' },
    { key: 'horseshoe', icon: Moon, desc: '7 cartas para guia completa' },
    { key: 'celtic_cross', icon: Sun, desc: 'La tirada mas completa' },
    { key: 'star', icon: Sparkles, desc: 'Esperanzas y suenos' },
    { key: 'tree_of_life', icon: TreeDeciduous, desc: 'Sabiduria cabalistica' },
  ];

  const selectSpread = async (key) => {
    const spread = spreads[key];
    if (spread.credits === 0 && !canReadFree) { toast.error('Ya realizaste tu lectura gratuita de hoy. Vuelve manana o adquiere creditos.'); return; }
    if (spread.credits > 0 && (!user || user.credits < spread.credits)) {
      if (!user) { toast.error('Inicia sesion para esta tirada'); return; }
      toast.error(`Necesitas ${spread.credits} creditos`); navigate('/credits'); return;
    }
    setSelectedSpread(key); setStage('shuffle');
  };

  const shuffleDeck = () => {
    setIsShuffling(true);
    setDeck([...deck].sort(() => Math.random() - 0.5));
    setTimeout(() => { setIsShuffling(false); toast.success('Cartas barajadas'); }, 2000);
  };

  const selectCard = (card) => {
    if (!selectedSpread) return;
    const spread = spreads[selectedSpread];
    if (selectedCards.length >= spread.cards) return;
    const newCard = { ...card, reversed: Math.random() > 0.5 };
    setSelectedCards([...selectedCards, newCard]);
    setDeck(deck.filter(c => c.id !== card.id));
    if (selectedCards.length + 1 >= spread.cards) setStage('reveal');
  };

  const getReading = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/tarot/reading`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ spread_type: selectedSpread, cards: selectedCards.map(c => ({ id: c.id, reversed: c.reversed })) })
      });
      if (!res.ok) { const error = await res.json(); toast.error(error.detail); return; }
      const data = await res.json();
      setInterpretation(data.interpretation); setStage('reading');
      if (user) await refreshUser();
    } catch (e) { toast.error('Error al obtener la lectura'); } finally { setLoading(false); }
  };

  const resetReading = () => {
    setSelectedSpread(null); setSelectedCards([]); setInterpretation(''); setStage('select');
    fetch(`${API}/tarot/cards`).then(res => res.json()).then(setDeck);
  };

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold gold-text mb-4" data-testid="tarot-title">Lectura de Tarot</h1>
          <p className="text-[var(--text-muted)]">
            {stage === 'select' && 'Selecciona tu tipo de tirada'}
            {stage === 'shuffle' && 'Baraja y selecciona tus cartas'}
            {stage === 'reveal' && 'Cartas seleccionadas. Lista para tu lectura?'}
            {stage === 'reading' && 'Tu interpretacion mistica'}
          </p>
        </div>

        {stage === 'select' && (
          <motion.div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {spreadList.map(({ key, icon: Icon, desc }) => {
              const spread = spreads[key]; if (!spread) return null;
              const canAccess = spread.credits === 0 || (user && user.credits >= spread.credits);
              return (
                <motion.div key={key} whileHover={{ scale: 1.02 }} className={`glass-card p-6 cursor-pointer ${!canAccess ? 'opacity-60' : ''}`} onClick={() => selectSpread(key)} data-testid={`spread-${key}`}>
                  <div className="flex items-start justify-between mb-4">
                    <Icon className="w-10 h-10 text-gold-300" />
                    {spread.credits === 0 ? (
                      <div className="text-right">
                        <span className={`text-xs px-2 py-1 rounded-full ${canReadFree ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}`}>{canReadFree ? 'GRATIS' : 'USADO HOY'}</span>
                        <p className="text-xs text-[var(--text-muted)] mt-1">1 por dia</p>
                      </div>
                    ) : (
                      <span className="credit-badge text-xs"><Coins className="w-3 h-3" /> {spread.credits}</span>
                    )}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{spread.name}</h3>
                  <p className="text-[var(--text-muted)] text-sm mb-2">{desc}</p>
                  <p className="text-sm text-purple-400">{spread.cards} cartas</p>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {stage === 'shuffle' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <button onClick={() => setStage('select')} className="btn-ghost text-sm">← Volver</button>
              <span className="text-[var(--text-muted)]">{selectedCards.length} / {spreads[selectedSpread]?.cards} cartas</span>
              <button onClick={shuffleDeck} disabled={isShuffling} className="btn-ghost flex items-center gap-2" data-testid="shuffle-btn">
                <Shuffle className={`w-4 h-4 ${isShuffling ? 'animate-spin' : ''}`} /> {isShuffling ? 'Barajando...' : 'Barajar'}
              </button>
            </div>
            {selectedCards.length > 0 && (
              <div className="glass-card p-6 mb-8">
                <h3 className="text-lg font-bold mb-4 text-purple-400">Cartas Seleccionadas</h3>
                <div className="flex flex-wrap gap-4 justify-center">
                  {selectedCards.map((card, i) => (
                    <motion.div key={i} initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: i * 0.1 }} className={`tarot-card flipped ${card.reversed ? 'reversed' : ''}`} data-testid={`selected-card-${i}`}>
                      <div className="tarot-card-inner">
                        <div className="tarot-card-back" />
                        <div className="tarot-card-front">
                          {card.image ? <img src={card.image} alt={card.name_es} className="w-full h-full object-cover rounded-lg" /> : <><div className="flex-1 flex items-center justify-center"><span className="text-4xl">{getCardSymbol(card.id)}</span></div><p className="text-xs text-center font-bold truncate w-full">{card.name_es}</p></>}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-4 justify-center mt-2">
                  {selectedCards.map((card, i) => <div key={i} className="w-[120px] text-center"><p className="text-sm font-bold text-purple-300">{card.name_es}</p></div>)}
                </div>
              </div>
            )}
            <div className="tarot-table p-4 sm:p-6" ref={deckContainerRef}>
              <h3 className="text-sm sm:text-lg font-bold mb-4 text-center text-purple-400">Toca una carta para seleccionarla</h3>
              <div className={`deck-rows ${isShuffling ? 'shuffling' : ''}`} data-testid="card-deck">
                {[0, 1].map(row => {
                  const half = Math.ceil(deck.length / 2);
                  const rowCards = row === 0 ? deck.slice(0, half) : deck.slice(half);
                  const cardW = 48; const count = rowCards.length;
                  const overlap = count > 1 ? Math.max(0, (count * cardW - deckWidth) / (count - 1)) : 0;
                  return (
                    <div key={row} className="deck-row">
                      {rowCards.map((card, i) => (
                        <motion.div key={card.id} className="deck-card-item" style={{ marginLeft: i === 0 ? 0 : `-${overlap}px` }}
                          whileHover={{ y: -12, scale: 1.1, zIndex: 50 }} whileTap={{ scale: 0.95 }} transition={{ duration: 0.12 }}
                          onClick={() => selectCard(card)} data-testid={`deck-card-${card.id}`}>
                          <div className="tarot-card-inner"><div className="tarot-card-back" /></div>
                        </motion.div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {stage === 'reveal' && (
          <motion.div className="text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="glass-card p-8 mb-8">
              <h3 className="text-xl font-bold mb-6 text-purple-400">Tus Cartas Seleccionadas</h3>
              <div className="flex flex-wrap gap-6 justify-center mb-4">
                {selectedCards.map((card, i) => (
                  <motion.div key={i} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.2, duration: 0.4 }} className={`tarot-card flipped ${card.reversed ? 'reversed' : ''}`}>
                    <div className="tarot-card-inner">
                      <div className="tarot-card-back" />
                      <div className="tarot-card-front relative">
                        {card.image ? <img src={card.image} alt={card.name_es} className="w-full h-full object-cover rounded-lg" /> : <><div className="flex-1 flex items-center justify-center"><span className="text-3xl">{getCardSymbol(card.id)}</span></div><p className="text-xs text-center font-bold">{card.name_es}</p></>}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
              <div className="flex flex-wrap gap-6 justify-center mb-8">
                {selectedCards.map((card, i) => <div key={i} className="w-[120px] text-center"><p className="text-xs text-purple-400 mb-1">{spreads[selectedSpread]?.positions[i]}</p><p className="text-sm font-bold text-white">{card.name_es}</p></div>)}
              </div>
              {loading ? (
                <div className="flex flex-col items-center gap-4 py-8">
                  <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}><Loader2 className="w-12 h-12 text-purple-400" /></motion.div>
                  <p className="text-purple-300 text-lg font-medium">Consultando las estrellas...</p>
                  <p className="text-[var(--text-muted)] text-sm">La interpretacion puede tomar hasta 30 segundos</p>
                </div>
              ) : (
                <button onClick={getReading} className="btn-gold" data-testid="get-reading-btn">Obtener Interpretacion</button>
              )}
            </div>
          </motion.div>
        )}

        {stage === 'reading' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="glass-card p-8">
              <h3 className="text-2xl font-bold mb-6 text-purple-400 text-center">Tu Lectura Mistica</h3>
              <div className="flex flex-wrap gap-4 justify-center mb-4">
                {selectedCards.map((card, i) => (
                  <div key={i} className={`tarot-card flipped ${card.reversed ? 'reversed' : ''}`}>
                    <div className="tarot-card-inner"><div className="tarot-card-back" /><div className="tarot-card-front">
                      {card.image ? <img src={card.image} alt={card.name_es} className="w-full h-full object-cover rounded-lg" /> : <><span className="text-2xl">{getCardSymbol(card.id)}</span><p className="text-xs font-bold">{card.name_es}</p></>}
                    </div></div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4 justify-center mb-8">
                {selectedCards.map((card, i) => <div key={i} className="w-[120px] text-center"><p className="text-xs text-purple-400">{spreads[selectedSpread]?.positions[i]}</p><p className="text-sm font-bold text-white">{card.name_es}</p></div>)}
              </div>
              <div className="bg-black/30 rounded-xl p-6 mb-8">
                <MarkdownRenderer content={interpretation} className="text-[var(--text-secondary)]" data-testid="interpretation" />
              </div>
              <div className="flex gap-4 justify-center flex-wrap">
                <button onClick={resetReading} className="btn-ghost flex items-center gap-2" data-testid="new-reading-btn"><RotateCcw className="w-4 h-4" /> Nueva Lectura</button>
                {user && <Link to="/dashboard" className="btn-gold inline-flex items-center">Ver Historial</Link>}
                <ShareButton type="tarot" content={{ spread_type: selectedSpread?.name || '', cards: selectedCards.map(c => ({ name: c.name || c.id, image: c.image, position: c.position })), interpretation }} title={`Lectura de Tarot: ${selectedSpread?.name || ''}`} description="Descubre lo que las cartas revelan sobre tu destino" />
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
